import type { VideoCallingSlice, VideoCallingState, MeetingMeta } from "../types";

/**
 * Initial meeting metadata.
 */
const initialMeetingMeta: MeetingMeta = {
  meetingId: null,
  calendarEventId: null,
  eventTitle: null,
  roomName: null,
  isHost: false,
};

/**
 * Initial video calling state.
 */
const initialState: VideoCallingState = {
  // Connection
  room: null,
  connectionState: "disconnected",
  connectionError: null,

  // Local tracks
  localVideoTrack: null,
  localAudioTrack: null,
  isVideoEnabled: true,
  isAudioEnabled: true,
  deviceError: null,

  // Participants
  participants: [],

  // Meeting info
  meetingMeta: initialMeetingMeta,

  // UI state
  isWaitingRoom: true,
  isVisible: false,
};

/**
 * Creates the video calling Zustand slice.
 *
 * Apps integrate this into their store by passing their own update and get functions.
 * This allows the slice to work with any Zustand store structure.
 *
 * @param updateSlice - Function to update the slice state
 * @param getSlice - Function to get the current slice state
 * @returns The video calling slice with initial state and actions
 *
 * @example
 * ```typescript
 * // In apps/web/src/store/index.ts
 * import { createVideoCallingSlice } from '@tellescope/video-calling';
 *
 * const useAppStore = create<AppStore>()((set, get) => ({
 *   // ... other slices
 *   videoCalling: createVideoCallingSlice(
 *     (updates) => set((state) => ({
 *       videoCalling: { ...state.videoCalling, ...updates }
 *     })),
 *     () => get().videoCalling
 *   ),
 * }));
 * ```
 */
export function createVideoCallingSlice(
  updateSlice: (updates: Partial<VideoCallingState>) => void,
  getSlice: () => VideoCallingSlice
): VideoCallingSlice {
  return {
    // Initial state
    ...initialState,

    // ============================================
    // Connection Actions
    // ============================================

    setRoom: (room) => updateSlice({ room }),

    setConnectionState: (connectionState) => updateSlice({ connectionState, connectionError: null }),

    setConnectionError: (connectionError) => updateSlice({ connectionError, connectionState: "error" }),

    // ============================================
    // Track Actions
    // ============================================

    setLocalVideoTrack: (localVideoTrack) => updateSlice({ localVideoTrack }),

    setLocalAudioTrack: (localAudioTrack) => updateSlice({ localAudioTrack }),

    setVideoEnabled: (isVideoEnabled) => updateSlice({ isVideoEnabled }),

    setAudioEnabled: (isAudioEnabled) => updateSlice({ isAudioEnabled }),

    setDeviceError: (deviceError) => updateSlice({ deviceError }),

    // ============================================
    // Participant Actions
    // ============================================

    addParticipant: (participant) => {
      const current = getSlice().participants;
      // Avoid duplicates
      if (current.some((p) => p.sid === participant.sid)) {
        return;
      }
      updateSlice({ participants: [...current, participant] });
    },

    removeParticipant: (sid) => {
      const current = getSlice().participants;
      updateSlice({ participants: current.filter((p) => p.sid !== sid) });
    },

    clearParticipants: () => updateSlice({ participants: [] }),

    // ============================================
    // Meeting Actions
    // ============================================

    setMeetingMeta: (meta) => {
      const current = getSlice().meetingMeta;
      updateSlice({ meetingMeta: { ...current, ...meta } });
    },

    clearMeetingMeta: () => updateSlice({ meetingMeta: initialMeetingMeta }),

    // ============================================
    // UI Actions
    // ============================================

    setIsWaitingRoom: (isWaitingRoom) => updateSlice({ isWaitingRoom }),

    setIsVisible: (isVisible) => updateSlice({ isVisible }),

    // ============================================
    // Reset Actions
    // ============================================

    resetVideoState: () => {
      const slice = getSlice();

      // Stop local tracks
      if (slice.localVideoTrack) {
        slice.localVideoTrack.stop();
      }
      if (slice.localAudioTrack) {
        slice.localAudioTrack.stop();
      }

      // Disconnect from room
      if (slice.room) {
        slice.room.disconnect();
      }

      // Reset to initial state
      updateSlice({ ...initialState });
    },

    reset: () => {
      const slice = getSlice();

      // Stop local tracks
      if (slice.localVideoTrack) {
        slice.localVideoTrack.stop();
      }
      if (slice.localAudioTrack) {
        slice.localAudioTrack.stop();
      }

      // Disconnect from room
      if (slice.room) {
        slice.room.disconnect();
      }

      // Reset to initial state
      updateSlice({ ...initialState });
    },
  };
}

/**
 * Export initial state for testing purposes.
 */
export { initialState as videoCallingInitialState };
